import { useState, useEffect } from "react";
import { BankAccount } from "../../types/banks/BankAccount";
import { FirestoreBankAccountRepository } from "../../../infrastructure/repositories/BankAccountRepository";
import { Family } from "../../types/Family";
import { ProviderUser } from "../../types/ProviderUser";

interface FamilyMemberAccounts {
  [memberId: string]: BankAccount[];
}

export const useFamilyMemberAccounts = (
  family: Family | null,
  familyMembers: Record<string, ProviderUser>,
  currentUserId: string | undefined,
) => {
  const [familyMemberAccounts, setFamilyMemberAccounts] =
    useState<FamilyMemberAccounts>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchFamilyMemberAccounts = async () => {
      if (family && Object.keys(familyMembers).length > 0) {
        const bankAccountRepo = new FirestoreBankAccountRepository();
        try {
          const accountPromises = family.members.map(async (memberId) => {
            if (memberId === currentUserId) return null;
            const accounts =
              await bankAccountRepo.getBankAccountsByAppUserId(memberId);
            return { memberId, accounts };
          });

          const results = await Promise.all(accountPromises);
          const accountsMap = results.reduce<FamilyMemberAccounts>(
            (acc, result) => {
              if (result) {
                acc[result.memberId] = result.accounts;
              }
              return acc;
            },
            {},
          );

          setFamilyMemberAccounts(accountsMap);
        } catch (err) {
          setError(err as Error);
          console.error("Error fetching family member accounts:", err);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchFamilyMemberAccounts();
  }, [family, familyMembers, currentUserId]);

  return { familyMemberAccounts, loading, error };
};
